import { z } from "zod";
import {
  PhoneAssessmentSchema,
  PhoneProposalSchema,
} from "../../../skills/leadcue-reply/scripts/lib/phone-call.ts";
import type { WorkPlan } from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";

const text = z.string().trim().min(1).max(4000);
export const PhoneOutcomeSchema = z.strictObject({
  id: text,
  revision: z.number().int().positive(),
  conversationRevision: z.number().int().nonnegative(),
  configRevision: z.number().int().nonnegative(),
  assessment: PhoneAssessmentSchema,
  fingerprint: text,
  runId: text,
  handling: z
    .strictObject({
      outcomeRevision: z.number().int().positive(),
      actorId: text,
      at: z.iso.datetime({ offset: true }),
      method: z.enum(["manual", "handoff"]),
      recipientId: text.nullable(),
    })
    .nullable(),
  changedAfterHandling: z.boolean(),
  correctedAt: z.iso.datetime({ offset: true }).nullable().default(null),
  correction: z.enum(["number", "withdrawal", "details"]).nullable().default(null),
  correctionEvidence: z.strictObject({ sourceId: text, quote: text }).nullable().default(null),
  correctionRevision: z.number().int().nonnegative().default(0),
});
export type PhoneOutcome = z.infer<typeof PhoneOutcomeSchema>;
export const PhoneSummarySchema = z.strictObject({
  state: z.enum(["collecting", "ambiguous", "ready", "withdrawn", "failed", "stale"]),
  reason: PhoneAssessmentSchema.shape.reason,
  handled: z.boolean(),
  changedAfterHandling: z.boolean(),
});
export type PhoneSummary = z.infer<typeof PhoneSummarySchema>;
const PhoneCommandSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("correct_details"),
    proposal: PhoneProposalSchema,
    sourceId: text,
    quote: text,
    reason: text,
  }),
  z.strictObject({
    type: z.literal("correct_number"),
    number: text,
    sourceId: text,
    quote: text,
    reason: text,
  }),
  z.strictObject({ type: z.literal("withdraw"), reason: text, sourceId: text, quote: text }),
  z.strictObject({ type: z.literal("handle"), method: z.enum(["manual", "handoff"]) }),
  z.strictObject({ type: z.literal("undo_handling"), reason: text }),
]);
export const PhoneCommandRequestSchema = z.strictObject({
  operationId: z.uuid(),
  expectedRevision: z.number().int().positive(),
  expectedPlanRevision: z.number().int().nonnegative(),
  command: PhoneCommandSchema,
});
export type PhoneCommandRequest = z.infer<typeof PhoneCommandRequestSchema>;
export function phoneSummary(
  outcome: PhoneOutcome | null,
  plan: WorkPlan,
  currentConfigRevision = plan.configRevision,
): PhoneSummary | null {
  if (!outcome || plan.goalKind !== "phone_call") return null;
  const stale =
    currentConfigRevision !== outcome.configRevision ||
    plan.runId !== outcome.runId ||
    outcome.configRevision !== plan.configRevision ||
    outcome.conversationRevision !== plan.conversationRevision;
  return {
    state: stale ? "stale" : outcome.assessment.state,
    reason: outcome.assessment.reason,
    handled: outcome.handling !== null,
    changedAfterHandling: outcome.changedAfterHandling,
  };
}
